import { useCallback, useState } from "react";
import { toast } from "sonner";
import { FileStack, ChevronDown, ListPlus } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { uploadChunks } from "../kit/PdfImport";
import { ImportReview, REVIEW_OPTS, reviewFrom, confirmBody } from "../kit/ImportReview";

const COLL = Object.fromEntries(REVIEW_OPTS.collections);
const label = (r) => ({ uploading: `Uploading ${r.pct}%`, reading: "Reading the cover…", review: "Check & add", adding: "Adding…", error: r.error, queued: "✓ In the build queue" }[r.state]);
const detected = (v) => v && [COLL[v.collection], v.destination, v.edition, ...(["researched", "founders"].includes(v.collection) ? [v.tier, `${v.days} days`, v.season] : []),
  v.collection === "standalone" ? v.topic : null, v.collection === "guide" ? (v.subjects || []).filter(Boolean).join(", ") : null].filter(Boolean).join(" · ");

const Row = ({ r, i, setRev, toggle, addOne }) => (
  <>
    <tr data-testid={`bulk-row-${i}`} className="border-t border-stone-100 align-middle text-sm">
      <td className="py-2 pr-3 max-w-[220px] truncate" title={r.file.name}>{r.file.name}</td>
      <td data-testid={`bulk-status-${i}`} className={`pr-3 text-xs ${r.state === "error" ? "text-red-500" : r.state === "queued" ? "text-emerald-700" : "text-stone-500"}`}>{label(r)}</td>
      <td data-testid={`bulk-detected-${i}`} className="text-xs text-stone-600">{detected(r.rev)}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {r.state === "review" && <>
          <button data-testid={`bulk-edit-${i}`} onClick={() => toggle(i)} className="inline-flex items-center gap-1 text-xs text-stone-600 underline mr-3">Check <ChevronDown size={12} className={r.open ? "rotate-180" : ""} /></button>
          <button data-testid={`bulk-add-${i}`} onClick={() => addOne(i)} className="rounded-sm border border-[#C9A847] px-3 py-1 text-xs font-semibold text-[#9A7B2C] hover:bg-[#FBF6E9]">Add to queue</button>
        </>}
      </td>
    </tr>
    {r.open && r.state === "review" && <tr><td colSpan={4} className="pt-2"><ImportReview r={r.rev} setR={(fn) => setRev(i, fn)} filename={r.file.name} /></td></tr>}
  </>
);

export const BulkImport = ({ reload }) => {
  const [rows, setRows] = useState([]);
  const patch = useCallback((i, p) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...(typeof p === "function" ? p(r) : p) } : r))), []);
  const setRev = useCallback((i, fn) => patch(i, (r) => ({ rev: typeof fn === "function" ? fn(r.rev) : fn })), [patch]);
  const add = async (files) => {
    const start = rows.length;
    const list = [...files].filter((f) => /\.(pdf|html?)$/i.test(f.name));
    setRows((rs) => [...rs, ...list.map((file) => ({ file, state: "uploading", pct: 0 }))]);
    for (let k = 0; k < list.length; k++) {
      const i = start + k;
      try {
        const id = await uploadChunks(list[k], { bulk: true }, (pct) => patch(i, { pct, state: pct === 100 ? "reading" : "uploading" }));
        const { data } = await http.post(`/imports/${id}/detect`);
        patch(i, { state: "review", rev: reviewFrom(data, id, data.collection || "researched"), open: data.collection === "founders" });
      } catch (e) { patch(i, { state: "error", error: errMsg(e) }); }
    }
  };
  const addOne = async (i, quiet) => {
    const r = rows[i];
    try {
      const body = confirmBody(r.rev, true);
      patch(i, { state: "adding" });
      await http.post(`/imports/${r.rev.iid}/confirm`, body);
      patch(i, { state: "queued", open: false });
      if (!quiet) { toast.success(`${r.rev.destination} added to the build queue`); reload?.(); }
      return true;
    } catch (e) {
      patch(i, { state: "review", open: true });
      toast.error(`${r.file.name}: ${e.response ? errMsg(e) : e.message}`);
      return false;
    }
  };
  const ready = rows.map((r, i) => (r.state === "review" ? i : -1)).filter((i) => i >= 0);
  const addAll = async () => {
    let n = 0;
    for (const i of ready) n += (await addOne(i, true)) ? 1 : 0;
    if (n) toast.success(`${n} file(s) added to the build queue — they convert one after another`);
    reload?.();
  };
  return (
    <div data-testid="bulk-import" className="rounded-xl border border-stone-200 bg-white p-5 mb-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="eyebrow flex items-center gap-2"><FileStack size={12} /> Bulk PDF / HTML import</div>
        <div className="ml-auto flex gap-2">
          {ready.length > 0 && <button data-testid="bulk-add-all" onClick={addAll} className="inline-flex items-center gap-2 rounded-sm bg-[#C9A847] px-4 py-2 text-sm font-semibold text-white hover:bg-[#B8973A]"><ListPlus size={14} /> Add all to queue ({ready.length})</button>}
          <label data-testid="bulk-import-pick" className="cursor-pointer rounded-sm border border-[#C9A847] px-4 py-2 text-sm font-semibold text-[#9A7B2C] hover:bg-[#FBF6E9]">
            Choose files<input data-testid="bulk-import-input" type="file" multiple accept="application/pdf,.pdf,text/html,.html,.htm" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
          </label>
        </div>
      </div>
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); add(e.dataTransfer.files); }} data-testid="bulk-import-drop"
        className="mt-3 rounded-lg border-2 border-dashed border-stone-200 p-4 text-center text-sm text-stone-400">Drop several kit PDFs or HTML files here — each is read and its collection detected; check the details, then add them to the build queue</div>
      {rows.length > 0 && (
        <div className="overflow-x-auto mt-3"><table className="w-full">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-stone-400"><th className="py-1">File</th><th>Status</th><th>Read from the file</th><th /></tr></thead>
          <tbody>{rows.map((r, i) => <Row key={i} r={r} i={i} setRev={setRev} toggle={(j) => patch(j, (x) => ({ open: !x.open }))} addOne={addOne} />)}</tbody>
        </table></div>
      )}
    </div>
  );
};
